import {pageAuthorizedByGroup} from "../fragments/PageAuthorization";
import {Link, useParams} from "react-router-dom";
import {useQuery} from "@apollo/client";
import {graphQLServices} from "../../services/GraphQLServices";
import React, {useState} from "react";
import {
	Box,
	Breadcrumbs,
	CircularProgress,
	Paper,
	Stack,
	TextField,
	Typography
} from "@mui/material";
import {cloneMutationData} from "../products/Utils";
import {PentaSplit} from "../../components/common/PentaSplit";
import {ProcessButton} from "../../components/common/input/ProcessButton";
import {EXISTS_PHRASE, GET_PHRASE_ITEM, PUT_PHRASE_ITEM} from "./Queries";
import {PhraseItem} from "./models/PhraseItem";
import {translationsEditorTargetStage} from "./TranslationsOverview";
import {TabPanel} from "../../components/common/TabPanel";
import {translateFunction} from "../../localization/TranslationService";

const translate = translateFunction("SureBasePortalTranslations");

export const PhraseItemDetails = pageAuthorizedByGroup(function () {
	const id = useParams()?.id;

	const {loading, data, error} = useQuery(GET_PHRASE_ITEM, {
		client: graphQLServices.translationsClient,
		fetchPolicy: "no-cache",
		variables: {
			stage: translationsEditorTargetStage,
			id: id,
		}
	});
	
	const [phraseItem, setPhraseItem] = useState<PhraseItem>();
	
	if (loading)
		return <Box sx={{display: "flex", width: "100%", height: "100%"}}>
			<CircularProgress style={{margin: "auto"}}/>
		</Box>;
	
	if (error)
		throw error;
	
	const nodes = data.phraseItems.nodes;
	if (nodes === null || nodes.length === 0)
		throw new Error(`PhraseItem with id ${id} was not found.`);
	
	const node: PhraseItem = nodes[0];
	if (!phraseItem) {
		setPhraseItem(node);
		return <Box sx={{display: "flex", width: "100%", height: "100%"}}>
			<CircularProgress style={{margin: "auto"}}/>
		</Box>;
	}
	const props = {phraseItem, setPhraseItem};
	const handleSave = async (existingItem:PhraseItem) => {
		await phraseExists(existingItem).then(async (x) => {
			if (x) {
				throw new Error(`Item already exists`);
			}
			
			await saveChanges(existingItem);
		});
	};
	
	return <PentaSplit
		top={<Breadcrumbs style={{margin: "5px"}}>
			<Link style={{color: "inherit", textDecoration: "unset"}} to="/translations_setup">{translate("Translations management")}</Link>
			<Link style={{color: "inherit", textDecoration: "unset"}} to="/translations_setup/phrases">{translate("Phrases")}</Link>
			<Typography color="text.primary">{id}</Typography>
		</Breadcrumbs>}
		center={<Paper style={{
			height: "100%",
		}}>
			<Stack style={{height: "100%"}}>
				<div style={{flexGrow: 1, overflow: "hidden"}}>
					<Information {...props as PhraseItemProps}/>
				</div>
				<div style={{flexGrow: 0, padding: "10px"}}>
					<ProcessButton
						defaultText={translate("Save")}
						inProgressText={translate("Saving")}
						completedText={translate("Saved")}
						resetKeys={[phraseItem]}
						variant={"outlined"}
						onClick={() => handleSave(phraseItem)}/>
				</div>
			</Stack>
		</Paper>}
	/>;
}, ["Admin", "Translations"]);

async function phraseExists(item: PhraseItem): Promise<boolean> {
	
	const existsItems = await graphQLServices.translationsClient.query(
		{
			query: EXISTS_PHRASE,
			variables: {
				stage: translationsEditorTargetStage,
				phrase: item.phrase,
			},
			fetchPolicy: "no-cache"
		}
	);
	
	const nodes: any[] = existsItems.data.phraseItems.nodes;
	return nodes.filter(x => x.id !== item.id).length > 0;
}

async function saveChanges(phraseItem: PhraseItem) {
	await graphQLServices.translationsClient.mutate({
		mutation: PUT_PHRASE_ITEM,
		variables: {
			stage: translationsEditorTargetStage,
			phrase: cloneMutationData(phraseItem),
		}
	});
}

interface PhraseItemProps {
	readonly phraseItem: PhraseItem;
	readonly setPhraseItem: (phraseItem: PhraseItem) => void;
}

function Information(props: PhraseItemProps) {
	return <TabPanel value={0} index={0}>
		<div style={{height: "100%", overflow: "auto"}}>
			<Stack spacing={2}>
				<div>{translate("Phrase Information")}</div>
				<TextField
					label={translate("Phrase")}
					value={props.phraseItem.phrase}
					variant={"standard"}
					onChange={event => props.setPhraseItem({...props.phraseItem, phrase: event.target.value})}
				/>
				{props.phraseItem.locationUrl && (
					<div>
						<a
							href={props.phraseItem.locationUrl}
							target="_blank"
							rel="noopener noreferrer"
							style={{ display: "block", marginTop: "8px" }}
						>
							{props.phraseItem.locationUrl}
						</a>
					</div>
				)}
			</Stack>
		</div>
	</TabPanel>;
}


export function openPhraseItemDetailsFor(id: string) {
	window.location.href = linkPhraseItemDetailsFor(id);
}

export function linkPhraseItemDetailsFor(id: string): string {
	return `/translations_setup/phrases/${encodeURIComponent(id)}`;
}
